import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query"
import { HTTPError } from 'ky'
import { parallel } from "../../../backend/src/libs/parallel.ts"
import { deserializeFatUser } from "@imago/model"
import { api } from "./_imago_api.ts"
import { CareQuery } from "./user-care.ts"
import { InventoryQuery } from "./user-inventory.ts"

export class PaymentError extends Error {}
PaymentError.prototype.name = 'PaymentError'

export function useFinalizeStripeRedirect() {
	const mutation = useFinalizeStripe()

	return {
		mutate: ({paymentIntent, redirectStatus}: {
			paymentIntent: string
			redirectStatus: string
		}) => {
			const intent = paymentIntent
			if (intent && redirectStatus === 'succeeded') {
				mutation.mutate({intent})
			}
		}
	}
}

export function useFinalizeStripe() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['finalize-stripe'],
		mutationFn: async ({intent}: {
			intent: string
		}) => {
			try {
				const patch = deserializeFatUser(await api.post('stripe/', {
					json: {
						intent
					},
				}).json())
				await parallel([
					CareQuery.setData(qc, [], patch.care!),
					InventoryQuery.setData(qc, [], patch.inventory!),
				])
			} catch (e) {
				if (e instanceof HTTPError) {
					if (e.response.status === 422) {
						const error = await e.response.json()
						throw new PaymentError(error.message, {cause: error})
					}
				}
				CareQuery.invalidate(qc, [])
				InventoryQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return mutation
}

export function useStripeFinalizationState() {
	const mutations = useMutationState({
		filters: {
			mutationKey: ['finalize-stripe'],
			status: 'pending',
		},
	})
	return {isPending: mutations.length ? true : false}
}
